import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { parse, stringify } from '../../../util/urlHelpers';

import css from './AccountDeletedNotice.module.css';

export const ACCOUNT_DELETED_PARAM = 'accountDeleted';
const AUTO_HIDE_MS = 8000;

/**
 * Confirmation toast shown on the landing page after ManageAccountPage
 * deletes the account. Deletion ends with a full page load (the store is
 * reset on logout), so the flag travels in the URL: ?accountDeleted=true.
 * The flag is removed from the URL once the toast closes, so a reload or a
 * shared link doesn't show it again.
 */
const AccountDeletedNotice = () => {
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const params = parse(location.search);
  const hasFlag = !!params[ACCOUNT_DELETED_PARAM];
  const [isOpen, setIsOpen] = useState(hasFlag);

  useEffect(() => {
    if (hasFlag) {
      setIsOpen(true);
    }
  }, [hasFlag]);

  const close = () => {
    setIsOpen(false);
    const { [ACCOUNT_DELETED_PARAM]: _flag, ...rest } = parse(location.search);
    const search = stringify(rest);
    history.replace(`${location.pathname}${search ? `?${search}` : ''}`);
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const timer = setTimeout(close, AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={css.root} role="status" aria-live="polite">
      <span className={css.icon} aria-hidden="true">
        <svg viewBox="0 0 20 20" width="20" height="20">
          <circle cx="10" cy="10" r="10" fill="currentColor" />
          <path
            d="M5.5 10.5l3 3 6-6.5"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <p className={css.message}>
        <strong className={css.label}>
          <FormattedMessage id="AccountDeletedNotice.label" />
        </strong>{' '}
        <FormattedMessage id="AccountDeletedNotice.message" />
      </p>
      <button
        type="button"
        className={css.close}
        onClick={close}
        aria-label={intl.formatMessage({ id: 'AccountDeletedNotice.close' })}
      >
        ×
      </button>
    </div>
  );
};

export default AccountDeletedNotice;
